import React from 'react';
import styled, { css } from 'styled-components';
import { colors, fonts, radii, shadows, icones } from './designSystem';
import { ServicePill } from './shared';
import BadgeBonusRepar from './shared/BadgeBonusRepar';
import {
  AvisEtoiles,
  LigneAtelier,
  useStatutDuJour,
} from './shared/InfosAtelier';
import { getShopUrl } from '../../utils/utils';
import {
  isFixerLiteEnabled,
  isInstantBooking,
  isOnlineBookable,
} from '../../annuaire/bookingState';
import { icone } from '../../annuaire/tunnel/ui';
import {
  paliersDeLaFiche,
  remiseMaxBonusRepar,
} from '../../annuaire/bonusRepar';
import { heure, libellePastille } from '../../annuaire/tunnel/creneaux';

/**
 * La carte d'un atelier dans le listing d'une ville (lot 3, « direction C
 * Agenda » validée par Clément le 22/09 :
 * `docs/designs/fiche-annuaire-rdv-lots23-liste.md`).
 *
 * Trois zones sur ordinateur, empilées sur mobile : la photo bord à bord ; le
 * nom, les avis, une ligne « type · Ouvert · horaires », l'adresse et les
 * services (seuls tags de la carte) ; à droite, pour un atelier réservable
 * en ligne, « Réparation à domicile », le mode et « Prendre rendez-vous »,
 * sinon « Voir la fiche ».
 *
 * Réutilisée par le module de réparation hors zone (porte d'entrée 2,
 * `docs/designs/reparation-hors-zone-liste.md`) : `partenaire` marque un
 * atelier Fixer Lite que Roulez Jeunesse ne garantit pas, `onPrendreRendezVous`
 * ouvre le tunnel sur place au lieu d'aller à la fiche, et `empile` garde la
 * disposition mobile dans une colonne trop étroite pour trois zones.
 */

const LARGE = '900px';

/** Les règles d'ordinateur, sauf pour une carte `empile`. */
const surGrandEcran = (regles) => (p) =>
  !p.$empile &&
  css`
    @media (min-width: ${LARGE}) {
      ${regles}
    }
  `;

const Card = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  background-color: ${colors.white};
  border-radius: ${radii.card};
  overflow: hidden;
  box-shadow: ${shadows.card};
  transition: box-shadow 0.2s ease;
  opacity: ${(p) => (p.$dimmed ? 0.75 : 1)};
  outline: ${(p) => {
    if (p.$mode === 'instant') return `2px solid ${colors.vert}`;
    if (p.$mode === 'enligne') return '2px solid #cfe2fc';
    return 'none';
  }};

  &:hover {
    box-shadow: 0 6px 20px rgba(49, 73, 89, 0.12);
  }

  ${surGrandEcran(css`
    grid-template-columns: 240px minmax(0, 1fr) 260px;
  `)}
`;

const Photo = styled.div`
  position: relative;
  min-height: 190px;
  background: linear-gradient(
    135deg,
    ${colors.vertPastel} 0%,
    ${colors.bleuPastel} 100%
  );

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img.vide {
    inset: auto;
    top: 50%;
    left: 50%;
    width: 60px;
    height: auto;
    transform: translate(-50%, -50%);
    opacity: 0.3;
  }

  ${surGrandEcran(css`
    min-height: 210px;
  `)}
`;

const Infos = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textSecondary};

  ${surGrandEcran(css`
    padding: 20px 22px;
  `)}
`;

const Nom = styled.h3`
  margin: 0;
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 1.2rem;
  line-height: 1.2;
  color: ${colors.textDark};
`;

/** Le nom mène à la fiche et couvre toute la carte ; les boutons passent dessus. */
const LienFiche = styled.a`
  color: inherit;
  text-decoration: none;

  /* Pas le vert clair global des liens au survol : illisible sur blanc. */
  &:hover,
  &:focus {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;

/* Les étiquettes sur la photo, empilées en haut à gauche : « Labellisé Bonus
   Répar » d'abord, car c'est ce qui distingue l'atelier dans la liste (retour
   de Clément du 14/10), puis « Atelier partenaire ».

   Porte d'entrée 2 : Roulez Jeunesse ne garantit rien de la réparation d'un
   atelier Fixer Lite (Clément, 22/09). On le dit au moment du choix, comme
   une information : un badge sur la photo, puis une ligne sous le nom. */
const Etiquettes = styled.div`
  position: absolute;
  z-index: 1;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BadgePartenaire = styled.span`
  padding: 6px 12px;
  border-radius: ${radii.pill};
  background: ${colors.white};
  box-shadow: 0 2px 8px rgba(49, 73, 89, 0.18);
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.78rem;
  color: ${colors.textDark};
`;

const Partenaire = styled.p`
  margin: -4px 0 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${colors.textSecondary};
`;

const Adresse = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;

  img {
    width: 14px;
    height: 16px;
    object-fit: contain;
    margin-top: 2px;
  }
`;

const Services = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

const Zone = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-top: 1px solid ${colors.borderLight};
  font-family: ${fonts.body};
  background: ${(p) => {
    if (p.$mode === 'instant') return '#f7ffdc';
    if (p.$mode === 'enligne') return '#f3f8ff';
    return colors.white;
  }};
  align-items: ${(p) => (p.$mode ? 'stretch' : 'center')};

  ${surGrandEcran(css`
    justify-content: center;
    padding: 22px 20px;
    border-top: none;
    border-left: 1px solid ${colors.borderLight};
  `)}
`;

const Mode = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  ${surGrandEcran(css`
    justify-content: flex-start;
  `)}

  font-size: 0.85rem;
  line-height: 1.35;
  color: ${colors.textSecondary};

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  b {
    display: block;
    font-size: 0.92rem;
    color: ${colors.textDark};
  }
`;

/* FIXERLIGHT-084 : le prochain horaire libre, sous la promesse de mode. Pas de
   `b` ici comme dans <Mode> — c'est une information d'appoint, pas le libellé
   principal de la zone. */
const ProchainHoraire = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.35;
  color: ${colors.textSecondary};

  ${surGrandEcran(css`
    text-align: left;
  `)}
`;

/* Le plus haut palier du Bonus Répar, sur le fond blanc de la description,
   sous les services : le badge sur la photo dit « labellisé », cette ligne dit
   ce que ça rapporte (retour de Clément du 14/10). Le barème complet est sur
   la fiche. */
const BonusLigne = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 0;
  font-size: 0.88rem;
  line-height: 1.3;
  color: ${colors.textSecondary};

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
  }

  b {
    font-weight: 700;
    color: ${colors.textDark};
  }
`;

/* Le bouton sur toute la largeur, « Voir la fiche » centré dessous, sur
   mobile comme dans la colonne de droite (retour de Clément du 22/09). */
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`;

const Bouton = styled.a`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 14px;
  background: ${(p) => (p.$fonce ? colors.bleuFonce : colors.vert)};
  color: ${(p) => (p.$fonce ? colors.white : colors.bleuFonce)};
  font-weight: 600;
  font-size: 0.92rem;
  white-space: nowrap;
  text-decoration: none;
  border: none;
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${(p) => (p.$fonce ? colors.white : colors.bleuFonce)};
  }

  ${surGrandEcran(css`
    flex: none;
  `)}
`;

const LienSecondaire = styled.a`
  color: ${colors.bleu};
  font-weight: 600;
  font-size: 0.88rem;
  white-space: nowrap;
  text-align: center;
  text-decoration: none;
`;

export default function ListingCard({
  shop,
  partenaire = false,
  onPrendreRendezVous = null,
  empile = false,
  nextSlot = null,
}) {
  const details = (shop && shop.details) || {};
  // Lus comme sur la fiche, pour que la carte et la fiche disent la même chose.
  const {
    statut: statutDuJour,
    horaires: horairesAujourdhui,
  } = useStatutDuJour(details);
  if (!shop) return null;

  const photos = details.photos || {};
  const mainPhoto =
    shop.cover_image || photos.s3_main || shop.profile_picture || null;
  const tousServices = details.services || [];
  const services = tousServices.slice(0, 4);
  const noteGoogle = details.note_google;
  const nbAvis = details.nb_avis_google;
  const statut = details.statut_etablissement;

  // Le nom court de l'atelier ; le titre SEO de la fiche fait souvent trois
  // lignes (retour de Clément du 22/09).
  // Le nom en base porte parfois un sous-titre (« X : achat et location… ») :
  // on garde ce qui précède.
  const nom = (shop.name || (details.seo && details.seo.h1_page) || '')
    .split(/\s[:–|]\s|\s-\s/)[0]
    .trim();
  // L'adresse en base contient souvent déjà « 67000 Strasbourg, France ».
  const rue = (shop.address || details.adresse_complete || '')
    .replace(/,\s*France$/i, '')
    .trim();
  const ville = [shop.postal_code, shop.city].filter(Boolean).join(' ');
  const adresse =
    ville && !rue.includes(shop.postal_code || shop.city || '')
      ? [rue, ville].filter(Boolean).join(', ')
      : rue || ville;

  const isClosed =
    statut === 'Fermé temporairement' || statut === 'Fermé définitivement';
  // Pas de grisé pour un atelier réservable en ligne sans photo : il grisait
  // aussi « Prendre rendez-vous » (retour de Clément du 22/09).
  let mode = null;
  if (isInstantBooking(shop)) mode = 'instant';
  else if (isOnlineBookable(shop)) mode = 'enligne';
  const dimmed = isClosed || (!mainPhoto && !mode);

  const shopUrl = getShopUrl(shop);
  const fixerLite = isFixerLiteEnabled(shop);
  const paliers = paliersDeLaFiche(shop);
  // Depuis le listing, la fiche s'ouvre avec le tunnel déjà ouvert.
  const rdvUrl = fixerLite ? `${shopUrl}?rdv=1` : shopUrl;

  // FIXERLIGHT-084 : le prochain horaire libre servi par
  // `GET /v2/public/shops/next-slots` (un appel pour tout le listing, fait
  // par AnnuaireCity — cette carte ne fait AUCUN appel elle-même). Mêmes
  // libellés que le tunnel de créneaux (`annuaire/tunnel/creneaux.js`),
  // aucun format de date neuf — seule la casse change : `libellePastille`
  // majuscule le premier mot pour une pastille isolée (« Aujourd’hui »), le
  // ticket veut une minuscule après les deux-points d'une phrase (« …
  // libre : aujourd'hui, … », « … : demain, … », son propre exemple).
  // Calculé même si `mode` est faux (comme `rdvUrl` juste au-dessus) : c'est
  // le rendu plus bas, dans la branche `mode ? … : …`, qui garde une fiche
  // non réservable de jamais promettre un horaire sans bouton pour le
  // prendre — pas une seconde garde ici, qui serait redondante avec elle
  // (mutation-testée : sans elle, ce module reste vert).
  const minuscule = (mot) =>
    mot ? mot.charAt(0).toLowerCase() + mot.slice(1) : mot;
  const prochainHoraire = nextSlot
    ? `Prochain horaire libre : ${minuscule(
        libellePastille(nextSlot),
      )}, ${heure(nextSlot)}`
    : null;

  return (
    <Card $dimmed={dimmed} $mode={mode} $empile={empile}>
      <Photo $empile={empile}>
        {(paliers || partenaire) && (
          <Etiquettes>
            {paliers && <BadgeBonusRepar />}
            {partenaire && (
              <BadgePartenaire>Atelier partenaire</BadgePartenaire>
            )}
          </Etiquettes>
        )}
        {mainPhoto ? (
          <img src={mainPhoto} alt={nom} loading="lazy" />
        ) : (
          <img className="vide" src={icones.velo} alt="" />
        )}
      </Photo>

      <Infos $empile={empile}>
        <Nom>
          <LienFiche href={shopUrl}>{nom}</LienFiche>
        </Nom>

        {partenaire && (
          <Partenaire>Tarifs et réparation gérés par l’atelier</Partenaire>
        )}

        <AvisEtoiles note={noteGoogle} nbAvis={nbAvis} />

        <LigneAtelier
          typologie={details.typologie_etablissement}
          statut={statutDuJour}
          horaires={horairesAujourdhui}
        />

        {adresse && (
          <Adresse>
            <img src={icones.localisation} alt="" />
            {adresse}
          </Adresse>
        )}

        {services.length > 0 && (
          <Services>
            {services.map((svc, i) => (
              <ServicePill key={i} name={svc} />
            ))}
            {tousServices.length > 4 && <span>+{tousServices.length - 4}</span>}
          </Services>
        )}

        {paliers && (
          <BonusLigne data-testid="bonus-repar">
            <img src={icone('TARIF_EUROS')} alt="" />
            <span>
              <b>Bonus Répar :</b> {remiseMaxBonusRepar(paliers).toLowerCase()}{' '}
              sur votre facture
            </span>
          </BonusLigne>
        )}
      </Infos>

      <Zone $mode={mode} $empile={empile}>
        {mode ? (
          <>
            <Mode $empile={empile}>
              <img
                src={icone(mode === 'instant' ? 'ENERGIE' : 'CALENDRIER')}
                alt=""
              />
              <span>
                {/* Fixer Lite = réparation à domicile en V1. */}
                <b>
                  {fixerLite ? 'Réparation à domicile' : 'Réservation en ligne'}
                </b>
                {mode === 'instant' && 'Confirmée instantanément'}
                {mode === 'enligne' && fixerLite && 'Confirmée par email'}
              </span>
            </Mode>
            {prochainHoraire && (
              <ProchainHoraire>{prochainHoraire}</ProchainHoraire>
            )}
            <Actions>
              {onPrendreRendezVous ? (
                <Bouton
                  as="button"
                  type="button"
                  onClick={() => onPrendreRendezVous(shop)}
                  $fonce={mode === 'enligne'}
                  $empile={empile}>
                  Prendre rendez-vous
                </Bouton>
              ) : (
                <Bouton
                  href={rdvUrl}
                  $fonce={mode === 'enligne'}
                  $empile={empile}>
                  Prendre rendez-vous
                </Bouton>
              )}
              <LienSecondaire href={shopUrl}>Voir la fiche</LienSecondaire>
            </Actions>
          </>
        ) : (
          <Bouton href={shopUrl} $empile={empile}>
            Voir la fiche →
          </Bouton>
        )}
      </Zone>
    </Card>
  );
}
